import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, SendHorizonal } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { copy } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatWidget = () => {
  const { lang } = useLanguage();
  const t = copy[lang].chat;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);
  const greeted = useRef(false);

  useEffect(() => {
    if (open && !greeted.current) {
      greeted.current = true;
      setMessages([{ role: "assistant", content: t.greeting }]);
    }
  }, [open, t.greeting]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, language: lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", content: t.error };
                return next;
              });
            } else if (parsed.delta) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + parsed.delta };
                return next;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: t.error };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 200, damping: 18 }}
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-button"
        className="fixed bottom-6 right-6 z-[100] flex h-14 w-14 items-center justify-center border border-acid bg-ink text-acid shadow-[0_0_30px_rgba(206,255,0,0.15)] transition-colors duration-300 hover:bg-acid hover:text-black"
        aria-label="Chat"
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[100] flex h-[480px] w-[calc(100vw-3rem)] max-w-sm flex-col border border-acid/30 bg-ink shadow-2xl"
            data-testid="chat-window"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-500/70" />
                <span className="h-2 w-2 rounded-full bg-yellow-500/70" />
                <span className="h-2 w-2 rounded-full bg-green-500/70" />
              </div>
              <p className="font-tty text-[10px] uppercase tracking-[0.2em] text-neutral-400">{t.title}</p>
              <span className="flex items-center gap-1.5 font-tty text-[10px] uppercase tracking-widest text-acid">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-acid" />
                {t.status}
              </span>
            </div>

            <div ref={scrollRef} data-lenis-prevent className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 font-tty text-xs leading-relaxed ${
                      msg.role === "user"
                        ? "bg-acid text-black"
                        : "border border-white/10 bg-surface text-neutral-200"
                    }`}
                    data-testid={`chat-message-${msg.role}-${i}`}
                  >
                    {msg.role === "assistant" && <span className="mr-1.5 text-brand-green">&gt;</span>}
                    {msg.content}
                    {msg.role === "assistant" && streaming && i === messages.length - 1 && (
                      <span className="animate-caret ml-0.5 inline-block h-3 w-1.5 translate-y-0.5 bg-acid" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex border-t border-white/10">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.placeholder}
                data-testid="chat-input"
                className="flex-1 bg-transparent px-4 py-3.5 font-tty text-xs text-white placeholder:text-neutral-600 outline-none"
              />
              <button
                onClick={send}
                disabled={streaming}
                data-testid="chat-send-button"
                className="border-l border-white/10 px-4 text-acid transition-colors duration-300 hover:bg-acid hover:text-black disabled:opacity-40"
                aria-label="Send"
              >
                <SendHorizonal className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
